// src/lib/partner/partnerInvitations.js

import { supabase } from "../../supabaseClient";

export const PARTNER_PROGRAMME_CONSENT_VERSION =
  "partner-programme-v1";

const INVITATION_HISTORY_DAYS = 30;
const INVITATION_EXPIRY_DAYS = 7;

function cleanText(value) {
  return String(value || "").trim();
}

function cleanEmail(value) {
  return cleanText(value).toLowerCase();
}

function cleanName(value) {
  return cleanText(value).replace(
    /\s+/g,
    " "
  );
}

function invitationIsVisible(
  invitation
) {
  const anchor =
    invitation?.invited_at ||
    invitation?.updated_at ||
    invitation?.created_at;

  if (!anchor) {
    return true;
  }

  const anchorTime =
    new Date(anchor).getTime();

  if (
    Number.isNaN(anchorTime)
  ) {
    return true;
  }

  const visibleAfter =
    Date.now() -
    INVITATION_HISTORY_DAYS *
      24 *
      60 *
      60 *
      1000;

  return anchorTime >= visibleAfter;
}

export async function fetchPartnerInvitations(
  cohortId = null
) {
  const cleanCohortId =
    cleanText(cohortId);

  const {
    data,
    error,
  } = await supabase.rpc(
    "get_my_partner_cohort_invitations",
    {
      p_cohort_id:
        cleanCohortId || null,
    }
  );

  if (error) {
    throw error;
  }

  const rows =
    Array.isArray(data)
      ? data
      : [];

  return rows.filter(
    invitationIsVisible
  );
}

export async function sendPartnerCohortInvite({
  cohortId,
  name,
  email,
}) {
  const cleanCohortId =
    cleanText(cohortId);

  const cleanInviteeName =
    cleanName(name);

  const cleanInviteeEmail =
    cleanEmail(email);

  if (!cleanCohortId) {
    throw new Error(
      "A cohort is required."
    );
  }

  if (!cleanInviteeName) {
    throw new Error(
      "An entrepreneur name is required."
    );
  }

  if (
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
      cleanInviteeEmail
    )
  ) {
    throw new Error(
      "A valid entrepreneur email address is required."
    );
  }

  const {
    data,
    error,
  } = await supabase.functions.invoke(
    "send-partner-cohort-invite",
    {
      body: {
        cohortId:
          cleanCohortId,

        name:
          cleanInviteeName,

        email:
          cleanInviteeEmail,

        // Backend remains authoritative and fixes this at 7 days.
        expiresInDays:
          INVITATION_EXPIRY_DAYS,
      },
    }
  );

  if (error) {
    throw error;
  }

  if (data?.error) {
    throw new Error(
      data.error
    );
  }

  return data || {};
}

export async function revokePartnerInvitation(
  invitationId
) {
  const cleanInvitationId =
    cleanText(invitationId);

  if (!cleanInvitationId) {
    throw new Error(
      "An invitation is required."
    );
  }

  const {
    data,
    error,
  } = await supabase.rpc(
    "revoke_partner_cohort_invitation",
    {
      p_invitation_id:
        cleanInvitationId,
    }
  );

  if (error) {
    throw error;
  }

  return data === true;
}

export async function getPartnerInvitationLink(
  invitationId
) {
  const cleanInvitationId =
    cleanText(invitationId);

  if (!cleanInvitationId) {
    throw new Error(
      "An invitation is required."
    );
  }

  const {
    data,
    error,
  } = await supabase.rpc(
    "get_partner_cohort_invitation_link_token",
    {
      p_invitation_id:
        cleanInvitationId,
    }
  );

  if (error) {
    throw error;
  }

  const token =
    cleanText(data);

  if (!token) {
    return "";
  }

  if (
    typeof window ===
      "undefined" ||
    !window.location?.origin
  ) {
    throw new Error(
      "The invitation link could not be generated."
    );
  }

  const origin =
    String(
      window.location.origin
    ).replace(/\/+$/, "");

  return (
    `${origin}` +
    `/accept-cohort-invite` +
    `?token=${encodeURIComponent(
      token
    )}`
  );
}

export async function getPartnerInviteContext(
  token
) {
  const cleanToken =
    cleanText(token);

  if (!cleanToken) {
    throw new Error(
      "The invitation token is required."
    );
  }

  const {
    data,
    error,
  } = await supabase.rpc(
    "get_partner_cohort_invite_context",
    {
      p_token:
        cleanToken,
    }
  );

  if (error) {
    throw error;
  }

  if (!data) {
    throw new Error(
      "The invitation is invalid or no longer available."
    );
  }

  return data;
}

export async function acceptPartnerInvitation(
  token,
  options = {}
) {
  const cleanToken =
    cleanText(token);

  if (!cleanToken) {
    throw new Error(
      "The invitation token is required."
    );
  }

  const consentGiven =
    options?.consentGiven ===
    true;

  if (consentGiven) {
    const consentVersion =
      cleanText(
        options?.consentVersion
      ) ||
      PARTNER_PROGRAMME_CONSENT_VERSION;

    const {
      data,
      error,
    } = await supabase.rpc(
      "accept_partner_cohort_invitation_with_consent",
      {
        p_token:
          cleanToken,

        p_consent_given:
          true,

        p_consent_version:
          consentVersion,
      }
    );

    if (error) {
      throw error;
    }

    return data || {};
  }

  const {
    data,
    error,
  } = await supabase.rpc(
    "accept_partner_cohort_invitation",
    {
      p_token:
        cleanToken,
    }
  );

  if (error) {
    throw error;
  }

  return data || {};
}